import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Modulo, PerfilUsuario, Usuario } from '@eleg360/types';
import { adminApi, type ConfiguracoesMandato, type NovoUsuario } from '@/lib/api/admin';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

const MODULOS: Array<{ v: Modulo; nome: string; desc: string; essencial?: boolean }> = [
  { v: 'gab', nome: 'Gabinete360', desc: 'Contatos, solicitações, agenda, ações e campanhas.', essencial: true },
  { v: 'eleg', nome: 'Eleg · Inteligência Eleitoral', desc: 'Votação, territórios, pesquisas e metas.' },
  { v: 'oto', nome: 'OTO · Estratégia com IA', desc: 'Persona, estratégias e agentes cognitivos.' },
];

const PERFIS: Array<{ v: PerfilUsuario; l: string }> = [
  { v: 'titular', l: 'Titular' },
  { v: 'admin', l: 'Administrador' },
  { v: 'assessor_atendimento', l: 'Assessor de atendimento' },
  { v: 'assessor_comunicacao', l: 'Assessor de comunicação' },
  { v: 'assessor_politico', l: 'Assessor político' },
  { v: 'articulador', l: 'Articulador' },
  { v: 'leitura', l: 'Somente leitura' },
];

/**
 * Titular e administrador têm acesso irrestrito por definição, e o servidor
 * recusa revogá-los na gestão de usuários e permissões: sem eles, o mandato
 * ficaria sem ninguém capaz de desfazer a mudança. A tela reflete isso em vez
 * de oferecer um clique que a API vai recusar.
 */
const IRRESTRITOS: PerfilUsuario[] = ['titular', 'admin'];

const CONFIG_VAZIA: ConfiguracoesMandato = {};

export function AdminPage() {
  const token = useAuthStore((s) => s.token) || '';
  const usuarioLogado = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<0 | 1 | 2>(0);
  const [modalUsuario, setModalUsuario] = useState<Usuario | 'novo' | null>(null);
  const [config, setConfig] = useState<ConfiguracoesMandato>(CONFIG_VAZIA);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
  });

  const [filtroRota, setFiltroRota] = useState('');
  const [soAlteradas, setSoAlteradas] = useState(false);

  const { data: matriz } = useQuery({
    queryKey: ['permissoes'],
    queryFn: () => adminApi.permissoes(token),
    enabled: !!token && tab === 1,
  });

  const definirPermissao = useMutation({
    mutationFn: (dados: { rota: string; perfil: PerfilUsuario; permitido?: boolean }) =>
      adminApi.definirPermissao(dados, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissoes'] });
      // O guard mantem cache de 30s por instancia: a mudanca ja vale no banco,
      // mas pode levar ate esse tempo para alcancar quem esta com sessao aberta.
      toast('Permissão atualizada. Pode levar até 30 segundos para valer em toda a API.');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao alterar a permissão.')),
  });

  const rotasDaMatriz = matriz?.rotas ?? [];
  const totalAlteradas = rotasDaMatriz.filter((r) => Object.keys(r.excecoes).length > 0).length;

  const rotasVisiveis = rotasDaMatriz.filter((r) => {
    if (soAlteradas && Object.keys(r.excecoes).length === 0) return false;
    if (!filtroRota.trim()) return true;
    return r.rota.toLowerCase().includes(filtroRota.trim().toLowerCase());
  });

  const { data: plano } = useQuery({
    queryKey: ['plano'],
    queryFn: () => adminApi.consumoDoPlano(token),
    enabled: !!token,
  });

  const { data: usuarios = [], isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => adminApi.listUsuarios(token),
    enabled: !!token,
  });

  useEffect(() => {
    if (mandato) setConfig(mandato.configuracoes || CONFIG_VAZIA);
  }, [mandato?.updated_at]);

  const invalidarMandato = () => queryClient.invalidateQueries({ queryKey: ['mandato'] });
  const invalidarUsuarios = () => {
    queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    // Criar ou desativar acesso muda a cota; sem invalidar, o painel de consumo
    // continuaria mostrando o numero de antes.
    queryClient.invalidateQueries({ queryKey: ['plano'] });
  };

  const mandatoMutation = useMutation({
    mutationFn: (dados: any) => adminApi.updateMandato(dados, token),
    onSuccess: () => {
      invalidarMandato();
      toast('Mandato atualizado.');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar o mandato.')),
  });

  const criarUsuario = useMutation({
    mutationFn: (dados: NovoUsuario) => adminApi.createUsuario(dados, token),
    onSuccess: () => {
      invalidarUsuarios();
      toast('Usuário criado com acesso ativo.');
      setModalUsuario(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao criar o usuário.')),
  });

  const atualizarUsuario = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) =>
      adminApi.updateUsuario(id, dados, token),
    onSuccess: () => {
      invalidarUsuarios();
      toast('Usuário atualizado.');
      setModalUsuario(null);
    },
    // O servidor recusa deixar o mandato sem titular ativo
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar o usuário.')),
  });

  const desativarUsuario = useMutation({
    mutationFn: (id: string) => adminApi.desativarUsuario(id, token),
    onSuccess: () => {
      invalidarUsuarios();
      toast('Acesso desativado.');
      setModalUsuario(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao desativar o acesso.')),
  });

  const solicitarRedefinicaoSenha = useMutation({
    mutationFn: (id: string) => adminApi.solicitarRedefinicaoSenha(id, token),
    onSuccess: () => toast('E-mail de definição de senha enviado.'),
    onError: (err: any) =>
      toast(erroDe(err, 'Não foi possível enviar o e-mail de definição de senha.')),
  });

  const toggleModulo = (m: Modulo) => {
    if (!mandato) return;
    const ativo = mandato.modulos.includes(m);
    const alvo = MODULOS.find((x) => x.v === m);

    if (ativo && alvo?.essencial) {
      toast('O Gabinete360 é o módulo essencial do plano e não pode ser desligado.');
      return;
    }
    const modulos = ativo ? mandato.modulos.filter((x) => x !== m) : [...mandato.modulos, m];
    mandatoMutation.mutate({ modulos });
  };

  const salvarConfig = () => mandatoMutation.mutate({ configuracoes: config });

  const trocarPerfil = (u: Usuario, perfil: PerfilUsuario) => {
    atualizarUsuario.mutate({ id: u.id, dados: { perfil } });
  };

  const alternarStatus = (u: Usuario) => {
    if (u.status === 'ativo') {
      if (window.confirm(`Desativar o acesso de ${u.nome}?`)) desativarUsuario.mutate(u.id);
    } else {
      atualizarUsuario.mutate({ id: u.id, dados: { status: 'ativo' } });
    }
  };

  return (
    <div className="page" id="p-admin">
      <div className="head">
        <div>
          <h1>Administração</h1>
          <p>
            Núcleo da plataforma: mandato, usuários, matriz RBAC de fonte única, módulos contratados
            e limiares operacionais. Clique nas células da matriz para alterar a permissão de um
            perfil.
          </p>
        </div>
        {tab === 1 && (
          <div className="act">
            <button className="btn pri" onClick={() => setModalUsuario('novo')}>
              + Novo usuário
            </button>
          </div>
        )}
      </div>

      <div className="tabs">
        <button className={tab === 0 ? 'on' : ''} onClick={() => setTab(0)}>
          Módulos
        </button>
        <button className={tab === 1 ? 'on' : ''} onClick={() => setTab(1)}>
          Usuários e RBAC
        </button>
        <button className={tab === 2 ? 'on' : ''} onClick={() => setTab(2)}>
          Limiares e políticas
        </button>
      </div>

      {/* ABA 0: MÓDULOS */}
      {tab === 0 && (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <h2>{mandato?.nome || 'Mandato'}</h2>
            <dl className="dl">
              <dt>Cargo</dt>
              <dd>{mandato?.cargo || '—'}</dd>
              <dt>Município</dt>
              <dd>
                {mandato?.cidade || '—'} · {mandato?.uf || '—'}
              </dd>
              <dt>Plano</dt>
              <dd>
                <span className="chip acc">{plano?.rotulo || mandato?.plano || '—'}</span>
              </dd>
            </dl>
          </div>

          {plano && (
            <div className="card" style={{ marginBottom: 12 }}>
              <h2>Consumo do plano</h2>
              <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 4 }}>
                Os limites são do plano contratado e valem no servidor: ao atingir o teto, a
                criação é recusada, não apenas escondida na tela.
              </p>

              <div className="grid g3" style={{ marginTop: 12 }}>
                {(
                  [
                    ['Usuários ativos', plano.usuarios],
                    ['Contatos', plano.contatos],
                    ['Mensagens no mês', plano.mensagens_mes],
                  ] as const
                ).map(([rotulo, r]) => {
                  const pct = r.percentual ?? 0;
                  return (
                    <div className="card kpi" key={rotulo}>
                      <div className="l">{rotulo}</div>
                      <div
                        className="v"
                        style={{
                          color:
                            r.limite === null
                              ? 'var(--ink)'
                              : pct >= 100
                              ? 'var(--crit)'
                              : pct >= 80
                              ? 'var(--warn)'
                              : 'var(--good)',
                        }}
                      >
                        {r.usado.toLocaleString('pt-BR')}
                        <span style={{ fontSize: 14, color: 'var(--ink3)' }}>
                          {r.limite === null ? '' : ` / ${r.limite.toLocaleString('pt-BR')}`}
                        </span>
                      </div>
                      <div className="d">
                        {/* Percentual de "sem limite" nao significa nada, entao
                            o Enterprise mostra a palavra em vez de um numero. */}
                        {r.limite === null ? 'sem limite neste plano' : `${pct}% da cota`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid g3">
            {MODULOS.map((m) => {
              const ativo = mandato?.modulos.includes(m.v) ?? false;
              return (
                <div key={m.v} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, fontSize: 15 }}>{m.nome}</h2>
                    <button
                      className={`tog ${ativo ? 'on' : ''}`}
                      style={{ cursor: 'pointer' }}
                      disabled={mandatoMutation.isPending}
                      onClick={() => toggleModulo(m.v)}
                    >
                      {ativo ? 'Ativo' : 'Inativo'}
                    </button>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--ink2)', marginTop: 8 }}>{m.desc}</p>
                  {m.essencial && (
                    <span className="chip neutral" style={{ fontSize: 11 }}>
                      essencial do plano
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="note" style={{ marginTop: 12 }}>
            Desligar um módulo bloqueia as rotas dele na API para todo o mandato, não só na
            interface.
            <Info titulo="O que a API devolve com o módulo desligado">
              O status devolvido, <b>402</b> ou <b>404</b>, é o escolhido na aba Limiares e
              políticas.
            </Info>
          </div>
        </>
      )}

      {/* ABA 1: USUÁRIOS E RBAC */}
      {tab === 1 && (
        <>
          <div className="card flat tw">
            <table>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Papel</th>
                  <th>Módulos</th>
                  <th>MFA</th>
                  <th>Situação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.id} className="row">
                    <td>
                      <b>{u.nome}</b>
                      <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{u.email}</div>
                    </td>
                    <td>
                      <select
                        className="sel sm"
                        value={u.perfil}
                        disabled={atualizarUsuario.isPending}
                        onChange={(e) => trocarPerfil(u, e.target.value as PerfilUsuario)}
                      >
                        {PERFIS.map((p) => (
                          <option key={p.v} value={p.v}>
                            {p.l}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {(u.modulos_permitidos || []).length
                        ? u.modulos_permitidos.map((m) => (
                            <span key={m} className="chip neutral" style={{ marginRight: 4 }}>
                              {m}
                            </span>
                          ))
                        : '—'}
                    </td>
                    <td>
                      <span className={`chip ${u.mfa_habilitado ? 'good' : 'warn'}`}>
                        {u.mfa_habilitado ? 'ativo' : 'inativo'}
                      </span>
                    </td>
                    <td>
                      <span className={`chip ${u.status === 'ativo' ? 'good' : 'neutral'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn sm" onClick={() => setModalUsuario(u)}>
                        ✏️ Editar
                      </button>{' '}
                      <button
                        className="btn sm"
                        disabled={solicitarRedefinicaoSenha.isPending || u.status !== 'ativo'}
                        title={
                          u.status !== 'ativo'
                            ? 'Ative o acesso antes de enviar o link de definição de senha'
                            : 'Envia um e-mail com um link de uso único para definir a senha'
                        }
                        onClick={() => {
                          if (window.confirm(`Enviar link de definição de senha para ${u.nome}?`)) {
                            solicitarRedefinicaoSenha.mutate(u.id);
                          }
                        }}
                      >
                        ✉️ Enviar link de senha
                      </button>{' '}
                      <button
                        className="btn sm"
                        style={u.status === 'ativo' ? { color: 'var(--crit)' } : undefined}
                        disabled={u.id === usuarioLogado?.id}
                        title={
                          u.id === usuarioLogado?.id
                            ? 'Você não pode desativar o próprio acesso'
                            : undefined
                        }
                        onClick={() => alternarStatus(u)}
                      >
                        {u.status === 'ativo' ? 'Desativar' : 'Reativar'}
                      </button>
                    </td>
                  </tr>
                ))}
                {isLoading && (
                  <tr>
                    <td colSpan={6} className="empty">
                      Carregando equipe...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h2>Matriz RBAC · aplicada no servidor</h2>
            <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 10px' }}>
              Cada célula é o acesso real: clicar altera o comportamento da API. O padrão vem do
              código; o que este mandato mudou aparece destacado, com <b>↺</b> para voltar atrás.
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <input
                className="in"
                style={{ flex: '1 1 260px' }}
                placeholder="Filtrar por rota (ex: contatos, DELETE, eleg)"
                value={filtroRota}
                onChange={(e) => setFiltroRota(e.target.value)}
              />
              <span
                className={`chip chipbtn ${soAlteradas ? 'acc on' : 'neutral'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setSoAlteradas((v) => !v)}
              >
                Só as alteradas ({totalAlteradas})
              </span>
            </div>

            <div style={{ overflowX: 'auto', maxHeight: 520 }}>
              <table className="matrix">
                <thead>
                  <tr>
                    <th>Rota</th>
                    {PERFIS.map((p) => (
                      <th key={p.v} style={{ fontSize: 11 }}>
                        {p.l}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rotasVisiveis.map((r) => (
                    <tr key={r.rota}>
                      <td className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        {r.rota}
                        {r.perfisPadrao.length === 0 && (
                          <span
                            className="chip neutral"
                            style={{ marginLeft: 6, fontSize: 10 }}
                            title="Sem @Roles: o código libera para qualquer usuário autenticado"
                          >
                            livre
                          </span>
                        )}
                      </td>
                      {PERFIS.map((p) => {
                        const irrestrito = IRRESTRITOS.includes(p.v);
                        const padrao =
                          irrestrito || r.perfisPadrao.length === 0 || r.perfisPadrao.includes(p.v);
                        const excecao = r.excecoes[p.v];
                        const efetivo = excecao ?? padrao;
                        const alterado = excecao !== undefined;

                        return (
                          <td key={p.v} style={{ textAlign: 'center' }}>
                            <button
                              className="btn sm"
                              disabled={irrestrito || definirPermissao.isPending}
                              title={
                                irrestrito
                                  ? 'Acesso irrestrito por definição: o servidor recusa revogar'
                                  : alterado
                                  ? `Alterado neste mandato (padrão: ${padrao ? 'permitido' : 'negado'}). Clique para voltar ao padrão.`
                                  : `Padrão do código: ${padrao ? 'permitido' : 'negado'}. Clique para inverter.`
                              }
                              style={{
                                padding: '1px 7px',
                                minWidth: 34,
                                color: efetivo ? 'var(--good)' : 'var(--ink3)',
                                border: alterado ? '1px solid var(--acc)' : '1px solid transparent',
                                background: 'transparent',
                              }}
                              onClick={() =>
                                definirPermissao.mutate({
                                  rota: r.rota,
                                  perfil: p.v,
                                  permitido: alterado ? undefined : !padrao,
                                })
                              }
                            >
                              {efetivo ? '✓' : '—'}
                              {alterado ? ' ↺' : ''}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {rotasVisiveis.length === 0 && (
                    <tr>
                      <td colSpan={PERFIS.length + 1} className="empty">
                        {soAlteradas
                          ? 'Nenhuma permissão alterada: tudo segue o padrão do código.'
                          : 'Nenhuma rota corresponde ao filtro.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ABA 2: LIMIARES E POLÍTICAS */}
      {tab === 2 && (
        <div className="card">
          <h2>Limiares operacionais do mandato</h2>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 14px' }}>
            Parâmetros de negócio aplicados às campanhas, réguas e atendimentos.
          </p>

          <div className="grid g2">
            <div className="field">
              <label>Limiar de aprovação de campanhas (destinatários)</label>
              <input
                type="number"
                min={0}
                className="in num"
                value={config.limiar_campanha ?? 0}
                onChange={(e) => setConfig({ ...config, limiar_campanha: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="field">
              <label>Teto de mensagens por contato / mês</label>
              <input
                type="number"
                min={0}
                className="in num"
                value={config.teto_mensagens_mes ?? 0}
                onChange={(e) =>
                  setConfig({ ...config, teto_mensagens_mes: Number(e.target.value) || 0 })
                }
              />
            </div>
            <div className="field">
              <label>Início do silêncio noturno</label>
              <input
                type="time"
                className="in"
                value={config.silencio_inicio || ''}
                onChange={(e) => setConfig({ ...config, silencio_inicio: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Fim do silêncio noturno</label>
              <input
                type="time"
                className="in"
                value={config.silencio_fim || ''}
                onChange={(e) => setConfig({ ...config, silencio_fim: e.target.value })}
              />
            </div>
            <div className="field">
              <label>SLA padrão de solicitações (dias)</label>
              <input
                type="number"
                min={1}
                className="in num"
                value={config.sla_padrao_dias ?? 1}
                onChange={(e) => setConfig({ ...config, sla_padrao_dias: Number(e.target.value) || 1 })}
              />
            </div>
            <div className="field">
              <label>k-anonimato mínimo (pessoas)</label>
              <input
                type="number"
                min={1}
                className="in num"
                value={config.k_anonimato ?? 1}
                onChange={(e) => setConfig({ ...config, k_anonimato: Number(e.target.value) || 1 })}
              />
              <small>
                {(config.k_anonimato ?? 1) <= 1
                  ? 'Com 1, nada é suprimido.'
                  : `Recortes de território × tema com menos de ${config.k_anonimato} solicitações aparecem como "·" na Matriz, no painel e em "O que a população está pedindo".`}{' '}
                Protege quem só vê os números agregados e não pode abrir os registros: os perfis
                de leitura, articulação e comunicação.
              </small>
            </div>
            <div className="field">
              <label>TTL de sessão (horas)</label>
              <input
                type="number"
                min={1}
                max={720}
                className="in num"
                value={config.ttl_sessao_horas ?? 1}
                onChange={(e) =>
                  setConfig({ ...config, ttl_sessao_horas: Number(e.target.value) || 1 })
                }
              />
              <small>
                Vale a partir do próximo login. Sessões já abertas seguem com a validade que
                receberam quando foram criadas.
              </small>
            </div>
            <div className="field">
              <label>Resposta para módulo não contratado</label>
              <select
                className="in"
                value={config.resposta_modulo_inativo ?? '402'}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    resposta_modulo_inativo: e.target.value as '402' | '404',
                  })
                }
              >
                <option value="402">402: informa que existe e não foi contratado</option>
                <option value="404">404: não revela que o módulo existe</option>
              </select>
              <small>
                {config.resposta_modulo_inativo === '404'
                  ? 'A rota responde como se não existisse. Some o aviso de contratação.'
                  : 'A rota diz qual módulo falta, permitindo oferecer a contratação.'}
              </small>
            </div>
          </div>

          <button
            className="btn pri"
            style={{ marginTop: 14 }}
            onClick={salvarConfig}
            disabled={mandatoMutation.isPending}
          >
            {mandatoMutation.isPending ? 'Salvando...' : 'Salvar limiares'}
          </button>
        </div>
      )}

      {modalUsuario && (
        <ModalUsuario
          usuario={modalUsuario === 'novo' ? null : modalUsuario}
          salvando={criarUsuario.isPending || atualizarUsuario.isPending}
          onCancelar={() => setModalUsuario(null)}
          onSalvar={(dados) => {
            if (modalUsuario === 'novo') criarUsuario.mutate(dados as NovoUsuario);
            else atualizarUsuario.mutate({ id: modalUsuario.id, dados });
          }}
        />
      )}
    </div>
  );
}

function ModalUsuario({
  usuario,
  salvando,
  onCancelar,
  onSalvar,
}: {
  usuario: Usuario | null;
  salvando: boolean;
  onCancelar: () => void;
  onSalvar: (dados: Partial<NovoUsuario>) => void;
}) {
  const [nome, setNome] = useState(usuario?.nome || '');
  const [email, setEmail] = useState(usuario?.email || '');
  const [senha, setSenha] = useState('');
  const [perfil, setPerfil] = useState<PerfilUsuario>(usuario?.perfil || 'leitura');
  const [modulos, setModulos] = useState<Modulo[]>(usuario?.modulos_permitidos || []);
  const [mfa, setMfa] = useState(usuario?.mfa_habilitado ?? true);

  const toggleModulo = (m: Modulo) =>
    setModulos((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || !email.trim()) {
      toast('Nome e e-mail são obrigatórios.');
      return;
    }
    if (!usuario && senha.length < 8) {
      toast('A senha inicial deve ter no mínimo 8 caracteres.');
      return;
    }
    if (usuario && senha && senha.length < 8) {
      toast('A nova senha deve ter no mínimo 8 caracteres.');
      return;
    }

    onSalvar({
      nome: nome.trim(),
      email: email.trim(),
      perfil,
      modulos_permitidos: modulos,
      mfa_habilitado: mfa,
      // Em edição, a senha só vai quando o campo foi preenchido
      ...(senha ? { senha } : {}),
    });
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={usuario ? 'Editar usuário' : 'Novo usuário'}
        aoFechar={onCancelar}
        rodape={
          <>
            <button type="button" className="btn" onClick={onCancelar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvando}>
              {salvando ? 'Salvando...' : usuario ? 'Salvar alterações' : 'Criar usuário'}
            </button>
          </>
        }
      >
        <div className="field">
          <label>Nome *</label>
          <input className="in" value={nome} onChange={(e) => setNome(e.target.value)} required />
        </div>

        <div className="field">
          <label>E-mail institucional *</label>
          <input
            className="in"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="field">
          <label>{usuario ? 'Nova senha (deixe vazio para manter)' : 'Senha inicial *'}</label>
          <input
            className="in"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="new-password"
            placeholder="Mínimo 8 caracteres"
          />
        </div>

        <div className="grid g2">
          <div className="field">
            <label>Papel</label>
            <select
              className="in"
              value={perfil}
              onChange={(e) => setPerfil(e.target.value as PerfilUsuario)}
            >
              {PERFIS.map((p) => (
                <option key={p.v} value={p.v}>
                  {p.l}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>MFA</label>
            <select className="in" value={mfa ? '1' : '0'} onChange={(e) => setMfa(e.target.value === '1')}>
              <option value="1">Exigir segundo fator</option>
              <option value="0">Não exigir</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>Módulos permitidos</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {MODULOS.map((m) => (
              <span
                key={m.v}
                className={`chip chipbtn ${modulos.includes(m.v) ? 'acc on' : 'neutral'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => toggleModulo(m.v)}
              >
                {m.nome} {modulos.includes(m.v) ? '✓' : '+'}
              </span>
            ))}
          </div>
        </div>

      </Drawer>
    </form>
  );
}
